import { useEffect, useState } from 'react';
import { bungie } from '../../lib/bungie';
import { items, tablesOf } from '../../lib/manifest';
import { useHub, usePrivate } from '../context';
import { loadQuests, questDone, type QuestItem } from '../character';
import type { Hub } from '../hub';
import { Asking, Bar, Card, Chip, Heading, ObjectiveStrip, Pic, SmallButton, Split } from '../ui';
import { PrivateGate } from './CharacterTab';

/** Port of the app's Quests tab: Bounties, All Quests, Exotics (private) and Pathfinder (public). */
export default function QuestsTab({ sub }: { sub: string }) {
  if (sub === 'Pathfinder') return <PathfinderPage />;
  return <PrivateGate title={sub} what="Your bounties and quests."><QuestList sub={sub} /></PrivateGate>;
}

const SORTS = ['Completion', 'Name', 'Expiry', 'Kind'];

function QuestList({ sub }: { sub: string }) {
  const h = useHub();
  const char = h.char!;
  const { data: quests, error, again } = usePrivate(`quests:${char.id}`, (t) => loadQuests(h.player!, t, char.id));
  const [find, setFind] = useState('');
  const [draft, setDraft] = useState('');
  const [sort, setSort] = useState(0);
  const bounties = sub === 'Bounties', exotics = sub === 'Exotics';
  let shown: QuestItem[] = (quests ?? []).filter((q) => !q.order && q.bounty === bounties && (!exotics || q.exotic));
  const rank = h.season ? h.hub!.progress(char.id, h.season.rewardProgression) : undefined;
  if (find) {
    const f = find.toLowerCase();
    shown = shown.filter((q) => [q.name, q.desc, q.typeName, ...q.objectives.map((o) => o.text)].some((t) => t.toLowerCase().includes(f)));
  }
  if (sort === 1) shown = [...shown].sort((a, b) => a.name.localeCompare(b.name));
  if (sort === 2) shown = [...shown].sort((a, b) => (a.expires?.getTime() ?? Infinity) - (b.expires?.getTime() ?? Infinity) || a.name.localeCompare(b.name));
  if (sort === 3) shown = [...shown].sort((a, b) => a.typeName.localeCompare(b.typeName) || a.name.localeCompare(b.name));
  return (
    <Split
      left={(
        <div className="g-stack" style={{ gap: 10 }}>
          <h1 className="g-title light">{bounties ? 'Bounties' : exotics ? 'Exotics' : 'All Quests'}</h1>
          <p className="g-text2 big14">{bounties ? 'The bounties your character is carrying, the ones closest to done first.' : exotics ? 'Exotic gear and catalyst quests your character is carrying.' : 'Every quest your character is carrying, with the step it is on.'}</p>
          <p className="g-dim small">Showing {char.cls}. Click your emblem in the header to switch character.</p>
          <div><SmallButton onClick={again}>Read again</SmallButton></div>
          {quests && <p className="small">{shown.length} carried, {shown.filter(questDone).length} ready to hand in.</p>}
          {bounties && rank && rank.size > 0 && (
            <>
              <div style={{ fontSize: 20, marginTop: 8 }}>{(rank.size - rank.into).toLocaleString()} XP</div>
              <p className="g-dim small">Experience needed for season rank {rank.level + 1}.</p>
              <Bar fraction={rank.into / rank.size} color="#ffffff" height={4} />
            </>
          )}
          <input className="g-input" style={{ marginTop: 10 }} placeholder="<enter name or objective>" value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && setFind(draft.trim())} onBlur={() => setFind(draft.trim())} aria-label="Find a quest" />
          <Heading style={{ marginTop: 8 }}>S O R T E D   B Y</Heading>
          <div className="g-chips">{SORTS.map((s, i) => <Chip key={s} on={sort === i} onClick={() => setSort(i)}>{s}</Chip>)}</div>
        </div>
      )}
      right={!quests ? <Asking text={error ?? 'Asking Bungie what your character is carrying...'} /> : shown.length === 0
        ? <p className="g-text2">{bounties ? 'No bounties on this character.' : exotics ? 'No exotic quests on this character.' : 'No quests on this character.'}</p>
        : (
          <div className="g-wrap">
            {shown.map((q, k) => {
              const done = questDone(q);
              const kind = q.questLine && q.questLine !== q.name ? `${q.typeName}  |  ${q.questLine}` : q.typeName;
              const left = q.expires ? q.expires.getTime() - Date.now() : 0;
              return (
                <Card key={`${q.hash}:${k}`} className="g-quest" done={done}>
                  <span className={`g-tile ${done ? 'gold' : ''}`} style={{ width: 52, height: 52 }}><Pic src={q.icon} /></span>
                  <div className="g-order-main" title={`${q.name}\n${q.desc}`}>
                    <div className="g-record-title"><b className={done ? 'good' : ''} style={{ fontSize: 15 }}>{q.name}</b>{q.tracked && <span className="g-tag" style={{ color: 'var(--info)' }}>TRACKED</span>}</div>
                    {kind && <div className="g-dim small" style={{ whiteSpace: 'pre' }}>{kind}</div>}
                    {q.desc && <p className="g-text2 clamp4" style={{ fontSize: 13, margin: 0 }}>{q.desc}</p>}
                    {q.objectives.map((o, i) => <ObjectiveStrip key={i} text={o.text} progress={o.progress} goal={o.goal > 0 ? o.goal : 1} done={o.done} />)}
                    {q.expires && (
                      <span className={`small ${left < 6 * 3_600_000 ? 'g-warn' : 'g-dim'}`}>
                        {left <= 0 ? 'Expired' : left < 48 * 3_600_000 ? `Expires in ${Math.floor(left / 3_600_000)} h ${Math.floor((left % 3_600_000) / 60_000)} min` : `Expires ${q.expires.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`}
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
    />
  );
}

// ---------------- Pathfinder ----------------

interface PathStep { hash: number; name: string; desc: string; icon: string; counts: string; progress: number; goal: number; done: boolean; claimed: boolean; onPath: boolean }
interface PathReward { name: string; icon: string; count: number }
interface PathBoard { hash: number; reward: PathStep; tiers: PathStep[][]; pathRewards: PathReward[] }
const fraction = (s: PathStep) => (s.done ? 1 : s.goal > 0 ? Math.min(1, s.progress / s.goal) : 0);

const PATH_ROOT = 1062988660;

async function loadBoard(hub: Hub, charId: string | undefined): Promise<PathBoard | null> {
  const [t, lite] = await Promise.all([tablesOf('DestinyPresentationNodeDefinition', 'DestinyRecordDefinition', 'DestinyObjectiveDefinition'), items()]);
  const boards: number[] = (t.DestinyPresentationNodeDefinition[PATH_ROOT]?.children?.presentationNodes ?? []).map((n: any) => n.presentationNodeHash).filter(Boolean);
  if (!boards.length) return null;
  const p = await bungie<any>(`/Destiny2/${hub.player.membershipType}/Profile/${hub.player.membershipId}/?components=700`);
  const mine = p.profilePresentationNodes?.data?.nodes ?? {};
  const perChar = charId ? p.characterPresentationNodes?.data?.[charId]?.nodes ?? {} : {};
  // state 1 = invisible: the boards the game is not showing this player
  const board = boards.find((b) => { const n = mine[b] ?? perChar[b]; return n && ((n.state ?? 0) & 1) === 0; });
  if (!board) return null;
  const hashes: number[] = (t.DestinyPresentationNodeDefinition[board]?.children?.records ?? []).map((r: any) => r.recordHash).filter(Boolean);
  if (hashes.length < 3) return null;
  const step = (h: number): PathStep => {
    const d = t.DestinyRecordDefinition[h]?.displayProperties ?? {};
    const r = hub.record(h, charId);
    const o = r?.objectives[0];
    return {
      hash: h, name: d.name ?? '', desc: d.description ?? '', icon: d.icon ?? '', counts: o ? t.DestinyObjectiveDefinition[o.hash]?.progressDescription ?? '' : '',
      progress: o?.progress ?? 0, goal: o?.goal ?? 0, done: !!r && (o ? o.done : r.done), claimed: !!r?.redeemed, onPath: !!r && (r.state & 2) === 0,
    };
  };
  const rewards = (h: number): PathReward[] => {
    const byItem = new Map<number, number>();
    for (const i of t.DestinyRecordDefinition[h]?.rewardItems ?? []) byItem.set(i.itemHash, (byItem.get(i.itemHash) ?? 0) + Math.max(1, i.quantity ?? 1));
    return [...byItem.entries()].map(([ih, count]) => ({ d: lite[ih], count })).filter((x) => x.d?.name)
      .map((x) => ({ name: x.d.name.replace(/\s*\(\{var:\d+\}[^)]*\)/g, ''), icon: x.d.icon, count: x.count }));
  };
  // the board's records in Bungie's order: the reset, the path reward, then rows of 2, 3, 4, 5, 6 ...
  const tiers: PathStep[][] = [];
  for (let at = 2, size = 2; at < hashes.length; at += size, size++) tiers.push(hashes.slice(at, at + size).map(step));
  return { hash: board, reward: step(hashes[1]), tiers, pathRewards: rewards(hashes[1]) };
}

function PathfinderPage() {
  const h = useHub();
  const [board, setBoard] = useState<PathBoard | null>();
  const [error, setError] = useState<string>();
  const [pins, setPins] = useState<number[]>([]);
  const [turn, setTurn] = useState(0);
  const [reading, setReading] = useState<PathStep | null | undefined>(undefined);
  const [lit, setLit] = useState(0);
  useEffect(() => {
    let live = true;
    setBoard(undefined); setError(undefined); setPins([]);
    loadBoard(h.hub!, h.char?.id).then((b) => { if (!live) return; setBoard(b); if (!b) setError('Bungie is not showing a Pathfinder board for this Guardian right now.'); })
      .catch((e) => live && setError(`The Pathfinder board could not be loaded (${e instanceof Error ? e.message : e}).`));
    return () => { live = false; };
  }, [h.hub, h.char?.id, turn]);

  const intro = (
    <>
      <h1 className="g-title">Pathfinder</h1>
      <p className="g-text2" style={{ fontSize: 13.5 }}>Finish objectives and claim them in the game, one in each column from left to right, each touching the last, until your path reaches the reward.</p>
    </>
  );
  if (!board) return <Split left={intro} right={<Asking text={error ?? 'Asking Bungie which Pathfinder board you are on...'} />} />;

  const cols = [...board.tiers].reverse();   // widest first
  const all = cols.flat();

  // The suggested path: one objective per column, each touching the next, with the most done already (claimed counts
  // double, so a path already begun is kept). Objectives clicked must be on it; when two cannot, the older one goes.
  const plan = (pinned: number[]): number[] => {
    const worth = (s: PathStep) => (s.claimed ? 2 : fraction(s));
    const NO = -1e9;
    const score = cols.map((c) => new Array<number>(c.length).fill(0));
    const next = cols.map((c) => new Array<number>(c.length).fill(0));
    for (let c = cols.length - 1; c >= 0; c--) {
      const pin = cols[c].findIndex((s) => pinned.includes(s.hash));
      for (let i = 0; i < cols[c].length; i++) {
        score[c][i] = pin >= 0 && pin !== i ? NO : worth(cols[c][i]);
        if (c === cols.length - 1) continue;
        // an objective touches the one above and the one beside it in the next, shorter column
        const pick = [i - 1, i].filter((j) => j >= 0 && j < cols[c + 1].length).sort((a, b) => score[c + 1][b] - score[c + 1][a])[0];
        next[c][i] = pick; score[c][i] += score[c + 1][pick];
      }
    }
    let at = 0;
    for (let i = 1; i < cols[0].length; i++) if (score[0][i] > score[0][at]) at = i;
    if (score[0][at] < NO / 2) return [];
    const path: number[] = [];
    for (let c = 0; c < cols.length; c++) { path.push(at); if (c < cols.length - 1) at = next[c][at]; }
    return path;
  };
  let used = pins;
  let best = cols.length ? plan(used) : [];
  while (!best.length && used.length > 1) { used = used.slice(1); best = plan(used); }
  if (!best.length && cols.length) { used = []; best = plan(used); }

  // geometry: every column is an arc round the reward (the point of the fan)
  const STEP = 150, GAP = 92, NODE = 46, LEFT = 110, TOP = 70;
  const rows = cols.length ? cols[0].length : 1;
  const midY = TOP + ((rows - 1) * GAP) / 2 + NODE / 2;
  const apexX = LEFT + cols.length * STEP + 60;
  const radius = (c: number) => apexX - (LEFT + c * STEP);
  const Y = (c: number, i: number) => midY + (i - (cols[c].length - 1) / 2) * GAP;
  const X = (c: number, i: number) => { const r = radius(c), dy = Y(c, i) - midY; return apexX - Math.sqrt(Math.max(1, r * r - dy * dy)); };
  const W = apexX + 170, H = TOP * 2 + (rows - 1) * GAP + NODE;
  const arc = (r: number, a: number) => `M ${apexX - r * Math.cos(a)} ${midY - r * Math.sin(a)} A ${r} ${r} 0 0 0 ${apexX - r * Math.cos(a)} ${midY + r * Math.sin(a)}`;
  const spread = Math.asin(Math.min(0.95, (((rows - 1) * GAP) / 2 + 70) / radius(0)));
  const roman = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];
  const click = (c: number, s: PathStep) => setPins((p) => (p.includes(s.hash) ? p.filter((x) => x !== s.hash) : [...p.filter((x) => !cols[c].some((o) => o.hash === x)), s.hash]));
  const prizeIcon = board.pathRewards[0]?.icon || board.reward.icon;

  const left = (
    <div className="g-stack" style={{ gap: 8 }}>
      {intro}
      <p className="small">{all.filter((s) => s.done).length} of {all.length} objectives done, {all.filter((s) => s.claimed).length} claimed.</p>
      <Heading style={{ marginTop: 12 }}>{(used.length ? 'Your planned path' : 'Suggested path').toUpperCase().split('').join(' ')}</Heading>
      {used.length > 0 && <div><SmallButton onClick={() => setPins([])}>Back to the suggested path</SmallButton></div>}
      {best.map((i, c) => {
        const s = cols[c][i];
        return (
          <div key={s.hash} className="g-pathentry" onPointerEnter={() => { setLit(s.hash); setReading(s); }} onPointerLeave={() => setLit(0)}>
            <div className="g-pathentry-head">
              <Pic src={s.icon} width={26} height={26} />
              <b style={{ color: s.claimed ? '#f3e3a0' : s.done ? 'var(--good)' : '#fff' }}>{s.name}</b>
              <span className={s.claimed ? 'g-warn' : s.done ? 'good' : 'g-text2'}>{s.claimed ? 'Claimed' : s.done ? 'Done' : `${s.progress.toLocaleString()}/${Math.max(1, s.goal).toLocaleString()}`}</span>
            </div>
            <Bar fraction={fraction(s)} color={s.claimed ? 'var(--warn)' : 'var(--good)'} height={2} />
            <p className="g-text2">{s.desc}</p>
          </div>
        );
      })}
      {board.pathRewards.length > 0 && (
        <>
          <Heading style={{ marginTop: 14 }}>P A T H   R E W A R D</Heading>
          <div className="g-wrap g6">{board.pathRewards.map((r) => <span key={r.name} title={r.count > 1 ? `${r.name}  x${r.count}` : r.name}><Pic src={r.icon} width={34} height={34} /></span>)}</div>
        </>
      )}
      <div style={{ marginTop: 12 }}><SmallButton onClick={() => setTurn((x) => x + 1)}>Read again</SmallButton></div>
    </div>
  );

  const read = reading === undefined ? null : reading;
  const right = (
    <>
      <svg className="g-pathboard" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }}>
        {/* the line art behind: an instrument's arcs, rays and scale, all drawn round the reward */}
        {cols.map((col, c) => <path key={`a${c}`} d={arc(radius(c), Math.min(1.2, Math.asin(Math.min(0.95, (((col.length - 1) * GAP) / 2 + 46) / radius(c)))))} className="pb-dash" />)}
        <path d={arc(radius(0) + 46, spread)} className="pb-strong" />
        <path d={arc(radius(0) + 58, spread)} className="pb-dots" />
        <path d={arc(radius(0) + 84, spread * 0.82)} className="pb-faint" />
        {Array.from({ length: 49 }, (_, k) => {
          const tk = k - 24, a = (spread * tk) / 24, r1 = radius(0) + 46, r2 = r1 - (tk % 4 === 0 ? 12 : 6);
          return <line key={`t${k}`} x1={apexX - r1 * Math.cos(a)} y1={midY + r1 * Math.sin(a)} x2={apexX - r2 * Math.cos(a)} y2={midY + r2 * Math.sin(a)} className="pb-strong" />;
        })}
        {[-spread, spread, 0].map((a) => <line key={`r${a}`} x1={apexX} y1={midY} x2={apexX - (radius(0) + 84) * Math.cos(a)} y2={midY + (radius(0) + 84) * Math.sin(a)} className={a === 0 ? 'pb-axis' : 'pb-ray'} />)}
        <circle cx={apexX} cy={midY} r={64} className="pb-strong" />
        <circle cx={apexX} cy={midY} r={80} className="pb-dash" />
        <circle cx={apexX} cy={midY} r={112} className="pb-faint" />
        <line x1={apexX + 40} y1={midY} x2={apexX + 165} y2={midY} className="pb-strong" />
        <line x1={apexX} y1={midY - 150} x2={apexX} y2={midY + 150} className="pb-axis" />
        {/* the lattice: every objective to its neighbours in the next column */}
        {cols.map((col, c) => col.map((_, i) => (c + 1 < cols.length
          ? [i - 1, i].filter((j) => j >= 0 && j < cols[c + 1].length).map((j) => <line key={`l${c}-${i}-${j}`} x1={X(c, i)} y1={Y(c, i)} x2={X(c + 1, j)} y2={Y(c + 1, j)} className="pb-lattice" />)
          : <line key={`l${c}-${i}`} x1={X(c, i)} y1={Y(c, i)} x2={apexX} y2={midY} className="pb-lattice" />)))}
        {/* the path over it, dotted gold (solid where it is already claimed) */}
        {best.map((i, c) => {
          const from = cols[c][i];
          const x2 = c + 1 < best.length ? X(c + 1, best[c + 1]) : apexX, y2 = c + 1 < best.length ? Y(c + 1, best[c + 1]) : midY;
          const solid = from.claimed && (c + 1 >= best.length || cols[c + 1][best[c + 1]].claimed);
          return <line key={`p${c}`} x1={X(c, i)} y1={Y(c, i)} x2={x2} y2={y2} className={solid ? 'pb-path solid' : 'pb-path'} />;
        })}
        {Array.from({ length: cols.length ? rows : 0 }, (_, i) => <text key={`n${i}`} x={X(0, i) - NODE / 2 - 14} y={Y(0, i) + 5} className="pb-roman">{roman[Math.min(i, roman.length - 1)]}</text>)}
        {cols.map((col, c) => col.map((s, i) => {
          const onBest = best[c] === i, pinned = used.includes(s.hash);
          const ring = s.claimed ? 'var(--warn)' : s.done ? 'var(--good)' : pinned ? '#e6ce6e' : s.onPath ? 'rgba(255,255,255,0.9)' : onBest ? 'rgba(230,206,110,0.7)' : 'rgba(255,255,255,0.35)';
          const fill = s.claimed ? '#3a3214' : s.done ? '#163524' : pinned ? '#2b2714' : '#14161b';
          const big = lit === s.hash;
          return (
            <g key={`d${s.hash}`} className="pb-disc" style={{ animationDelay: `${c * 110 + i * 20}ms` }}
              transform={`translate(${X(c, i)} ${Y(c, i)}) scale(${big ? 1.28 : 1})`}
              onPointerEnter={() => { setLit(s.hash); setReading(s); }} onPointerLeave={() => setLit(0)} onClick={() => click(c, s)}>
              <circle r={NODE / 2} fill={fill} stroke={ring} strokeWidth={s.claimed || s.done || pinned ? 2.5 : 1.5} />
              <image href={s.icon ? `https://www.bungie.net${s.icon}` : undefined} x={-13} y={-13} width={26} height={26} opacity={s.done || s.onPath || onBest ? 1 : 0.7} />
              {!s.done && s.goal > 1 && s.progress > 0 && (
                <g transform={`translate(${-NODE / 2 + 4} ${NODE / 2 + 4})`}>
                  <rect width={NODE - 8} height={3} rx={1.5} fill="rgba(255,255,255,0.15)" />
                  <rect width={(NODE - 8) * fraction(s)} height={3} rx={1.5} fill="var(--good)" />
                </g>
              )}
            </g>
          );
        }))}
        <g transform={`translate(${apexX} ${midY})`} onPointerEnter={() => setReading(null)} className="pb-prize">
          <rect x={-33} y={-33} width={66} height={66} transform="rotate(45)" fill="none" stroke="rgba(255,255,255,0.35)" />
          <circle r={35} fill="#14161b" stroke={board.reward.claimed ? 'var(--warn)' : board.reward.done ? 'var(--good)' : 'rgba(255,255,255,0.8)'} strokeWidth={2} />
          {prizeIcon && <image href={`https://www.bungie.net${prizeIcon}`} x={-23} y={-23} width={46} height={46} />}
        </g>
      </svg>
      <Card className="g-pathread">
        <Pic src={read === null ? prizeIcon : read?.icon} width={40} height={40} />
        <div>
          {reading === undefined ? (
            <><b>Point at an objective to read it</b><p className="g-text2 small">Click one to plan your path through it. Click it again to let it go.</p></>
          ) : read === null ? (
            <>
              <b>Path reward</b>
              <p className="g-text2 small">{board.reward.claimed ? 'Claimed' : board.reward.done ? 'Your path is complete: claim it in the game' : 'Reached when your path of claimed objectives gets here'}</p>
              <p className="g-text2">{board.pathRewards.map((r) => (r.count > 1 ? `${r.name} x${r.count}` : r.name)).join(',  ')}</p>
            </>
          ) : (
            <>
              <b>{read.name}</b>
              <p className="g-text2 small">
                {read.goal > 0 ? `${read.counts || 'Progress'}: ${read.progress.toLocaleString()} / ${read.goal.toLocaleString()}.  ` : ''}
                {read.claimed ? 'Claimed.' : read.done ? 'Done: claim it in the game.' : read.onPath ? 'Can be claimed once done.' : 'Not next to your path yet (it still counts progress).'}
                {used.includes(read.hash) ? '  On your planned path: click it again to let it go.' : '  Click it to plan your path through it.'}
              </p>
              <p className="g-text2">{read.desc}</p>
            </>
          )}
        </div>
      </Card>
    </>
  );
  return <div className="g-split" style={{ gridTemplateColumns: 'minmax(0, 30fr) minmax(0, 70fr)' }}><div className="g-split-left">{left}</div><div className="g-split-right">{right}</div></div>;
}

